<template>
  <el-drawer
    title="Notifications"
    :visible.sync="showDrawer"
    :with-header="true"
    :before-close="handleClose"
    :size="drawerWidth"
  >
    <div class="px-8 pt-2">
      <section
        ref="notificationsSection"
        class="content-area lg:md:max-h-150 2xl:max-h-170 py-1.5 -mt-3 relative"
        style="overflow-y: auto"
      >
        <div class="grid grid-cols-12 gap-x-2 background p-1.5 rounded-lg">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            class="col-span-6 flex justify-center text-13px py-2.5 cursor-pointer transition-all duration-150 ease-linear"
            :class="activeClass(tab.key)"
            @click="setActiveTab(tab.key)"
          >
            {{ tab.label }}
            <span
              class="pl-2"
              v-if="tab.showCount && unReadNotificationsCount > 0"
            >
              ({{ unReadNotificationsCount }})</span
            >
          </div>
        </div>
        <div class="mt-3">
          <div v-if="activeTab === 'all' || activeTab === 'unread'">
            <div
              v-for="(group, date) in groupedNotifications"
              :key="date"
              class="py-3"
            >
              <p
                class="p-2 border rounded-md font-normal text-black-10 bg-gray-26"
              >
                {{ date }}
              </p>
              <div v-for="(item, index) in group" :key="index" class="border-b">
                <div
                  class="cursor-pointer"
                  @click="handleReadANotification(item)"
                >
                  <div class="flex justify-between items-center">
                    <div class="flex items-center gap-x-2 p-2">
                      <dot-icon v-if="!item.isRead" />
                      <p
                        style="word-break: normal"
                        class="capitalize text-black-10"
                        :class="
                          item.isRead ? 'font-thin text-sm' : 'font-semibold'
                        "
                      >
                        {{ item.title }}
                      </p>
                    </div>
                    <p
                      style="word-break: normal"
                      class="text-sm font-light capitalize text-gray-muted"
                    >
                      {{ parseRelativeTime(item.createdAt) }}
                    </p>
                  </div>
                  <p
                    style="word-break: normal"
                    class="text-sm font-normal px-2 pb-2 text-gray-muted"
                  >
                    {{ item.message }}
                  </p>
                </div>
              </div>
            </div>
            <div
              v-if="filteredNotifications.length === 0"
              class="mt-10 pt-10 font-medium mx-auto text-center"
            >
              <div class="flex flex-col items-center">
                <empty-state />
                <div>
                  <p class="mt-3">
                    You have no
                    {{ isUnreadNotification ? 'unread' : '' }} notifications
                  </p>
                  <p class="text-xs mx-auto">
                    Your
                    {{ isUnreadNotification ? 'unread' : '' }} notifications
                    will show up here once you have them
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="loadMore" class="flex items-center justify-center">
            <spinner
              size="medium"
              :spacing="40"
              :line-fg-color="$primaryColor"
            />
          </div>
        </div>
      </section>
      <div
        v-if="unReadNotificationsCount > 0"
        class="fixed bottom-0 border-t right-0 flex justify-end my-5"
        :style="{ width: drawerWidth }"
      >
        <div
          @click="handleMarkAllNotifications"
          class="border py-2 text-sm text-center w-36 mt-5 mb-3 mx-5 rounded-lg font-normal cursor-pointer"
        >
          Mark All As Read
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import tenant from '@/mixins/tenant.js'
import notification from '@/mixins/notification.js'

import { FormatDate, ParseRelativeTime } from '@/utils/helpers.js'
import { TRANSACTION_ROUTES } from '@/utils/constants.js'

export default {
  name: 'Drawer',
  mixins: [tenant, notification],
  data () {
    return {
      scrollTimeout: null,
      routeType: '',
      routeId: '',
      notificationCategory: '',
      notificationId: '',
      breakpoints,
      isLoading: false,
      loadMore: false,
      activeTab: 'all',
      tabs: [
        { key: 'all', label: 'All', showCount: false },
        { key: 'unread', label: 'Unread', showCount: true }
      ],
      drawerOpenedByNotification: false,
      routeStatus: ''
    }
  },
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    }
  },
  components: {
    EmptyState: () => import('@/assets/v2/icons/EmptyState2'),
    DotIcon: () => import('@/assets/v2/icons/DotIcon')
  },
  computed: {
    drawerWidth () {
      return breakpoints.lgAndUp ? '30%' : breakpoints.mdAndUp ? '70%' : '90%'
    },
    isReferenceTypeBulkPayout () {
      return this.routeType === this.$transactionType.BULK_PAYOUT
    },
    isReferenceTypeTransfer () {
      return this.routeType === this.$transactionType.TRANSFER
    },
    isReferenceTypeTransaction () {
      return TRANSACTION_ROUTES.includes(this.routeType)
    },
    isReferenceTypePaymentLinks () {
      return this.routeType === this.$transactionType.RECEIVE_PAYMENT
    },
    isCategoryAccount () {
      return this.notificationCategory === 'account'
    },
    sectionRef () {
      return this.$refs.notificationsSection
    },
    totalCounts () {
      return this.$store.state.notification.allNotifications.page.totalCount
    },
    isUnreadNotification () {
      return this.activeTab === 'unread'
    }
  },
  methods: {
    // Function to load data based on whether it's the next or previous page
    loadData (isNextPage) {
      // Check if not already loading and no scroll timeout is set
      if (!this.loadMore && !this.scrollTimeout) {
        this.loadMore = true
        this.counter += isNextPage ? 3 : 0
        // Set a timeout to prevent consecutive calls within a short duration
        this.scrollTimeout = setTimeout(() => {
          // Call the function to get all notifications and handle loading state
          if (this.activeTab === 'all') {
            this.getAllNotifications().finally(() => {
              this.loadMore = false
              // Reset the scroll timeout
              this.scrollTimeout = null
            })
          } else {
            this.getUnreadNotifications().finally(() => {
              this.loadMore = false
              // Reset the scroll timeout
              this.scrollTimeout = null
            })
          }
        }, 2000)
      }
    },
    handleScroll () {
      // Get reference to the notifications section
      // Calculate the scroll position percentage
      const percentage = Math.round(
        (this.sectionRef.scrollTop * 100) /
          (this.sectionRef.scrollHeight - this.sectionRef.offsetHeight)
      )
      // Check if the user has scrolled to the bottom or top 2%
      if (percentage > 98 && this.counter < this.totalCounts) {
        // Load more data for the next page
        this.loadData(true)
      } else if (percentage < 2 && this.counter > this.totalCounts) {
        // Load previous data for the previous page
        this.loadData(false)
      }
    },
    formatDate (val) {
      return FormatDate(val)
    },
    parseRelativeTime (timeToPick) {
      return ParseRelativeTime(timeToPick)
    },
    unreadMessagesList () {
      this.$store.dispatch('common/setUnreadMessages', this.unReadNotifications)
    },
    activeClass (tab) {
      return this.activeTab === tab ? 'active-class' : ''
    },
    handleClose () {
      this.$emit('closeDrawer')
    },
    toggleShow (arg) {
      this[arg] = !this[arg]
    },
    setActiveTab (tab) {
      this.activeTab = tab
    },
    handleNotificationRoute () {
      if (this.isReferenceTypeBulkPayout && !this.isCategoryAccount) {
        return this.$router.push(`/payout/${this.routeId}`)
      } else if (this.isReferenceTypePaymentLinks) {
        return this.$router.push(`/payment-link-details/${this.routeId}`)
      } else if (this.isReferenceTypeTransaction && !this.isCategoryAccount) {
        return this.$router.replace({
          name: 'transactions',
          params: {
            id: this.routeId,
            type: this.routeType,
            status: this.routeStatus
          }
        })
      }
    },
    handleReadANotification (item) {
      this.routeId = item.referenceID
      this.routeType = item.referenceType
      this.notificationId = item.id
      this.routeStatus = item.details.status
      this.notificationCategory = item.category
      this.handleMarkANotificationAsRead()
    },
    handleMarkANotificationAsRead () {
      this.isLoading = true
      this.$store
        .dispatch('notification/markANotificationAsRead', this.notificationId)
        .then(() => {
          this.isLoading = false
          this.drawerOpenedByNotification = true
          this.$store.dispatch(
            'notification/setDrawerOpenedByNotification',
            this.drawerOpenedByNotification
          )
          this.handleClose()
          this.handleNotificationRoute()
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    handleMarkAllNotifications () {
      this.isLoading = true
      this.$store
        .dispatch('notification/markAllNotificationsAsRead')
        .then(() => {
          this.isLoading = false
          this.handleClose()
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    appendScroll () {
      this.$nextTick(() => {
        this.sectionRef.addEventListener('scroll', this.handleScroll)
      })
    },
    removeScroll () {
      if (this.sectionRef) {
        this.sectionRef.removeEventListener('scroll', this.handleScroll)
      }
    }
  },
  beforeMount () {
    this.getAllNotifications()
    this.getUnreadNotifications()
  },
  mounted () {
    this.appendScroll()
  },
  destroyed () {
    this.removeScroll()
  }
}
</script>

<style scoped>
.content-area {
  max-height: 75vh;
}
.background {
  background: var(--background-muted);
}
.active-class {
  text-align: center;
  background: #ffffff;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  font-weight: 600;
  @apply text-primary;
}
>>> .el-drawer__header > :first-child {
  font-size: 1.1rem;
  color: var(--foreground-default);
  font-weight: 600;
}
>>> .el-drawer__header {
  color: var(--foreground-default);
  font-size: 1.2rem;
  padding-bottom: 1rem;
  padding-top: 2rem;
  padding-left: 1.4rem;
  font-weight: 700;
  border-bottom: 1px solid var(--border-main);
  margin-bottom: 20px;
}
>>> .el-drawer {
  background: var(--background-canvas) !important;
}
</style>
